<script lang="ts">
	import { sanitizeInputs } from '$lib/util/util';
	import {
		takeProfitInput,
		isTakeProfitInputValid,
		longOrShort,
		estimatedProfit,
		executionType,
		triggerInputForTrade,
		gainPercentage,
		takeProfitErrorMessage
	} from '../store.js';
	import { currentMarket, loadingPostTradeDetails } from '../../store.js';
	import BigNumber from 'bignumber.js';
	import { onDestroy, onMount } from 'svelte';
	import { DEFAULT_TAKE_PROFIT_GAIN_PERCENTAGE } from '../../constants.js';
	import ProcessingIcon from '$lib/util/ProcessingIcon.svelte';
	import { marketsConfig } from '$lib/config/markets.js';
	import { fly } from 'svelte/transition';

	let _takeProfitInput: string;
	let _gainPercentage: string = DEFAULT_TAKE_PROFIT_GAIN_PERCENTAGE;

	let currentPriceLimit: string;
	let currentPriceTrigger: string;

	$: currentPriceLimit = $currentMarket
		? $executionType != 'Market'
			? $triggerInputForTrade
			: $currentMarket.safePrice
		: '0';

	$: currentPriceTrigger = $currentMarket
		? $executionType != 'Market'
			? $triggerInputForTrade
			: $currentMarket.safePrice
		: '0';

	let _isTakeProfitInputValid = false;
	let _takeProfitErrorMessage = '';

	let gainPercentageEventListner = () => {};
	let takeProfitEventListner = () => {};

	$: _isTakeProfitInputValid = $isTakeProfitInputValid;
	$: _takeProfitErrorMessage = $takeProfitErrorMessage;

	onMount(() => {
		_gainPercentage = DEFAULT_TAKE_PROFIT_GAIN_PERCENTAGE;
		gainPercentage.set(_gainPercentage);

		handleGainPercentageInput();

		gainPercentageEventListner = currentMarket.subscribe((val) => {
			val && syncGainPercentageValue();
			gainPercentage.set(_gainPercentage);
		});
	});

	const syncTakeProfitValue = () => {
		if (!currentPriceLimit || new BigNumber(_gainPercentage).isNaN()) {
			_takeProfitInput = '';
			return;
		}

		_takeProfitInput = $currentMarket
			? $longOrShort === 'Long'
				? new BigNumber(1)
						.plus(new BigNumber(_gainPercentage).div(100))
						.multipliedBy(currentPriceLimit)
						.div(1e18)
						.toFixed(marketsConfig[$currentMarket.asset].showDecimals + 2)
						.toString()
				: new BigNumber(1)
						.minus(new BigNumber(_gainPercentage).div(100))
						.multipliedBy(currentPriceLimit)
						.div(1e18)
						.toFixed(marketsConfig[$currentMarket.asset].showDecimals + 2)
						.toString()
			: '0';
	};

	const syncGainPercentageValue = () => {
		_gainPercentage =
			currentPriceLimit && new BigNumber(_takeProfitInput).isGreaterThan(0)
				? $longOrShort === 'Long'
					? new BigNumber(_takeProfitInput)
							.multipliedBy(1e18)
							.minus(currentPriceLimit)
							.div(currentPriceLimit)
							.multipliedBy(100)
							.toFixed(4)
							.toString()
					: new BigNumber(currentPriceLimit)
							.minus(new BigNumber(_takeProfitInput).multipliedBy(1e18))
							.div(currentPriceLimit)
							.multipliedBy(100)
							.toFixed(4)
							.toString()
				: '';
	};

	const handleTakeProfitInput = () => {
		_takeProfitInput = sanitizeInputs(_takeProfitInput);
		takeProfitInput.set(_takeProfitInput || '0');
		syncGainPercentageValue();
		gainPercentage.set(_gainPercentage || '0');
	};

	const handleGainPercentageInput = () => {
		_gainPercentage = sanitizeInputs(_gainPercentage);
		gainPercentage.set(_gainPercentage);
		syncTakeProfitValue();
		takeProfitInput.set(_takeProfitInput || '0');
	};

	const onGainInputFocus = () => {
		gainPercentageEventListner();
		takeProfitEventListner();
		takeProfitEventListner = currentMarket.subscribe((val) => {
			if (val) {
				syncTakeProfitValue();
				takeProfitInput.set(_takeProfitInput || '0');
			}
		});
	};

	const onTakeProfitInputFocus = () => {
		takeProfitEventListner();
		gainPercentageEventListner();
		gainPercentageEventListner = currentMarket.subscribe((val) => {
			if (val) {
				syncGainPercentageValue();
				gainPercentage.set(_gainPercentage || '0');
			}
		});
	};

	onDestroy(() => {
		takeProfitInput.set('0');
		gainPercentageEventListner();
		takeProfitEventListner();
	});
</script>

<div class="flex gap-2 mt-2">
	<div
		class="bg-fx-grey-800 p-2 rounded-md flex-1 flex justify-between items-center"
		class:border={!_isTakeProfitInputValid}
		class:border-fx-red={!_isTakeProfitInputValid}
	>
		<div class="flex-1 flex items-center gap-1.5">
			<img
				src="./less_than_icon.svg"
				alt="less than icon"
				class="w-2.5"
				class:-scale-x-100={!($longOrShort == 'Long')}
			/>
			<div class="flex items-center">
				<span class="text-xs">$</span>
				<input
					inputmode="decimal"
					autocomplete="off"
					autocorrect="off"
					pattern="^[0-9]*[.,]?[0-9]*$"
					minlength="1"
					maxlength="79"
					spellcheck="false"
					type="string"
					placeholder="0"
					data-testid="open-position-amount"
					class="bg-transparent text-fx-white font-medium outline-none text-xs w-full"
					bind:value={_takeProfitInput}
					on:input={handleTakeProfitInput}
					on:focus={onTakeProfitInputFocus}
				/>
			</div>
		</div>

		<div class="text-xs text-fx-grey-400"><span class="mr-2">|</span> Take Profit</div>
	</div>
	<div
		class="bg-fx-grey-800 p-2 rounded-md flex-1 flex justify-between items-center"
		class:border={!_isTakeProfitInputValid}
		class:border-fx-red={!_isTakeProfitInputValid}
	>
		<div class="flex-1">
			<input
				inputmode="decimal"
				autocomplete="off"
				autocorrect="off"
				pattern="^[0-9]*[.,]?[0-9]*$"
				minlength="1"
				maxlength="79"
				spellcheck="false"
				type="string"
				placeholder="0"
				data-testid="open-position-amount"
				class="bg-transparent text-fx-white font-medium outline-none text-xs w-full"
				bind:value={_gainPercentage}
				on:input={handleGainPercentageInput}
				on:focus={onGainInputFocus}
			/>
		</div>

		<div class="text-xs text-fx-grey-400"><span class="mr-2">|</span> Gain %</div>
	</div>
</div>
{#if !_isTakeProfitInputValid}
	<div
		class="text-xxs text-fx-red mt-1 absolute font-semibold flex items-center gap-1"
		transition:fly={{ y: -10, duration: 200 }}
	>
		{_takeProfitErrorMessage}
		<img src="/caution.png" class="w-2 inline-block" />
	</div>
{/if}

<div class="flex justify-between my-4">
	<div class="text-fx-grey-400 text-xs font-medium">
		{`Estimated ${parseFloat($estimatedProfit) >= 0 ? 'Profit' : 'Loss'}`}
	</div>
	<div class="flex gap-2">
		{#if $loadingPostTradeDetails}
			<ProcessingIcon />
		{/if}
		{#if _isTakeProfitInputValid}
			<div
				class="{parseFloat($estimatedProfit) >= 0
					? 'text-fx-gradient-23'
					: 'text-fx-gradient-22'} text-xs font-medium"
			>
				{`${parseFloat($estimatedProfit) >= 0 ? '+' : '-'}$${Math.abs(
					parseFloat($estimatedProfit)
				).toFixed(2)}`}
			</div>
		{:else}
			<div class="text-fx-gradient-23 text-xs font-medium">$0.0</div>
		{/if}
	</div>
</div>

<style>
</style>
